import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { AuthLayout } from './AuthLayout';
import { ArrowLeft } from 'lucide-react';

const MIN_PASSWORD_LENGTH = 6;

export function ResetPasswordForm() {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [resolving, setResolving] = useState(true);

  useEffect(() => {
    let mounted = true;

    const checkSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (mounted && session) {
        setShowForm(true);
      }
      if (mounted) {
        setResolving(false);
      }
    };

    checkSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (mounted && event === 'PASSWORD_RECOVERY' && session) {
        setShowForm(true);
      }
      if (mounted && event === 'PASSWORD_RECOVERY') {
        setResolving(false);
      }
    });

    const timer = setTimeout(() => {
      if (mounted) setResolving(false);
    }, 2000);

    return () => {
      mounted = false;
      subscription.unsubscribe();
      clearTimeout(timer);
    };
  }, []);

  const hasRecoveryHash = () => {
    const hash = window.location.hash;
    return hash && (hash.includes('type=recovery') || hash.includes('access_token'));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setIsLoading(true);
    try {
      const { error: updateError } = await supabase.auth.updateUser({ password });
      if (updateError) throw updateError;
      await supabase.auth.signOut();
      navigate('/login', { replace: true });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to update password. Please try again.';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  if (resolving && hasRecoveryHash()) {
    return (
      <AuthLayout title="Reset your password">
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#8B5CF6]" />
        </div>
      </AuthLayout>
    );
  }

  if (!showForm && !hasRecoveryHash()) {
    return (
      <AuthLayout title="Reset your password">
        <div className="absolute top-8 left-8">
          <Link
            to="/login"
            className="inline-flex items-center text-gray-600 hover:text-white transition-colors"
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            Back to sign in
          </Link>
        </div>
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            This link is invalid or has expired. Request a new password reset link.
          </p>
          <div className="text-center">
            <Link
              to="/forgot-password"
              className="text-sm text-[#8B5CF6] hover:text-[#9D72F7]"
            >
              Request new reset link
            </Link>
          </div>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Set new password">
      <div className="absolute top-8 left-8">
        <Link
          to="/login"
          className="inline-flex items-center text-gray-600 hover:text-white transition-colors"
        >
          <ArrowLeft className="h-5 w-5 mr-2" />
          Back to sign in
        </Link>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {error && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        <div>
          <label
            htmlFor="password"
            className="block text-sm font-medium text-gray-700"
          >
            New password
          </label>
          <input
            id="password"
            type="password"
            required
            minLength={MIN_PASSWORD_LENGTH}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 sm:text-sm"
            placeholder="••••••••"
            disabled={isLoading}
          />
          <p className="mt-1 text-xs text-gray-500">
            At least {MIN_PASSWORD_LENGTH} characters
          </p>
        </div>

        <div>
          <label
            htmlFor="confirmPassword"
            className="block text-sm font-medium text-gray-700"
          >
            Confirm password
          </label>
          <input
            id="confirmPassword"
            type="password"
            required
            minLength={MIN_PASSWORD_LENGTH}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 sm:text-sm"
            placeholder="••••••••"
            disabled={isLoading}
          />
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#8B5CF6] hover:bg-[#9D72F7] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#8B5CF6] disabled:opacity-75 disabled:cursor-not-allowed"
        >
          {isLoading ? 'Updating...' : 'Update password'}
        </button>
      </form>
    </AuthLayout>
  );
}
